import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import QRCode from 'qrcode';
import templatePdf from '../assets/voucher-template.pdf';

const LOCATION_DETAILS = {
  1: {
    name: 'Prime Ink Tattoo Essen',
    address: 'Girardetstr. 8, 45131 Essen',
    instagram: '@prime_ink_tattoo_essen',
    email: 'info@prime-ink-essen.de',
    phone: '0201 85891229',
    website: 'www.prime-ink-essen.de',
  },

  2: {
    name: 'Prime Ink Tattoo Münster',
    address: 'Marktallee 46a, 48165 Münster',
    instagram: '@prime_ink_tattoo_muenster',
    email: 'info@prime-ink-muenster.de',
    phone: '0152 54586264',
    website: 'www.prime-ink-muenster.de',
  },

  3: {
    name: 'Prime Ink Tattoo Dortmund',
    address: 'Ostenhellweg 49, 44135 Dortmund',
    instagram: '@prime_ink_tattoo_dortmund',
    email: 'info@prime-ink-dortmund.de',
    phone: '0152 54586264',
    website: 'www.prime-ink-dortmund.de',
  },
};

function getLocation(voucher) {
  const locationId = Number(
    voucher.location_id ||
    voucher.locationId ||
    voucher.location?.id
  );

  const fallback = LOCATION_DETAILS[locationId] || LOCATION_DETAILS[3];

  return {
    name:
      voucher.location_name ||
      voucher.location?.name ||
      fallback.name,

    address:
      voucher.location_address ||
      voucher.location?.address ||
      fallback.address,

    instagram:
      voucher.location_instagram ||
      voucher.location?.instagram ||
      fallback.instagram,

    email:
      voucher.location_email ||
      voucher.location?.email ||
      fallback.email,

    phone:
      voucher.location_phone ||
      voucher.location?.phone ||
      fallback.phone,

    website:
      voucher.location_website ||
      voucher.location?.website ||
      fallback.website,
  };
}

function getVoucherDate(voucher) {
  const rawDate =
    voucher.paid_at ||
    voucher.issued_at ||
    voucher.created_at ||
    Date.now();

  const date = new Date(rawDate);

  return Number.isNaN(date.getTime())
    ? new Date()
    : date;
}

function getVoucherNumber(voucher) {
  const date = getVoucherDate(voucher);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());

  const id = String(voucher.id || 0).padStart(4, '0');

  return `PI-${day}${month}${year}-${id}`;
}

function fitFontSize(
  text,
  font,
  maxWidth,
  preferred,
  minimum = 5
) {
  let size = preferred;

  while (
    size > minimum &&
    font.widthOfTextAtSize(text, size) > maxWidth
  ) {
    size -= 0.25;
  }

  return size;
}

function drawCenteredText(
  page,
  text,
  font,
  size,
  x,
  y,
  width
) {
  const textWidth = font.widthOfTextAtSize(text, size);

  page.drawText(text, {
    x: x + Math.max(0, (width - textWidth) / 2),
    y,
    size,
    font,
    color: rgb(0.05, 0.05, 0.05),
  });
}

function coverWhite(page, x, y, width, height) {
  page.drawRectangle({
    x,
    y,
    width,
    height,
    color: rgb(1, 1, 1),
  });
}

function buildQrValue(voucherNumber) {
  return `VOUCHER:${voucherNumber}`;
}

async function createQrPngBytes(value) {
  const dataUrl = await QRCode.toDataURL(value, {
    errorCorrectionLevel: 'M',
    margin: 0,
    width: 360,
    color: {
      dark: '#111111',
      light: '#FFFFFF',
    },
  });

  const response = await fetch(dataUrl);

  if (!response.ok) {
    throw new Error('Der QR-Code konnte nicht erstellt werden.');
  }

  return response.arrayBuffer();
}
export async function createVoucherPdfBytes(voucher) {
  if (!voucher) {
    throw new Error('Kein Gutschein ausgewählt.');
  }

  const templateResponse = await fetch(templatePdf, {
    cache: 'no-store',
  });

  if (!templateResponse.ok) {
    throw new Error(
      `Die Gutschein-PDF-Vorlage konnte nicht geladen werden (${templateResponse.status}).`
    );
  }

  const templateBytes =
    await templateResponse.arrayBuffer();

  const header = new TextDecoder()
    .decode(templateBytes.slice(0, 5))
    .trim();

  if (!header.startsWith('%PDF-')) {
    throw new Error(
      'Die geladene Gutschein-Vorlage ist keine gültige PDF-Datei.'
    );
  }

  const pdfDoc = await PDFDocument.load(templateBytes);
  const page = pdfDoc.getPages()[0];

  const regular = await pdfDoc.embedFont(
    StandardFonts.Helvetica
  );

  const bold = await pdfDoc.embedFont(
    StandardFonts.HelveticaBold
  );

  const recipient = String(
    voucher.recipient_name ||
    voucher.recipientName ||
    voucher.buyer_name ||
    'Nicht angegeben'
  ).trim();

  const amount = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(voucher.amount || 0));

  const voucherNumber = getVoucherNumber(voucher);
  const location = getLocation(voucher);


  /*
   * Name
   */

  const recipientSize = fitFontSize(
    recipient,
    bold,
    82,
    14,
    8
  );

  drawCenteredText(
    page,
    recipient,
    bold,
    recipientSize,
    299,
    163,
    83
  );

  /*
   * Betrag
   */

  const amountSize = fitFontSize(
    amount,
    regular,
    40,
    20,
    12
  );

  drawCenteredText(
    page,
    amount,
    regular,
    amountSize,
    319,
    130,
    40
  );

  /*
   * Der aktuelle PDF-Entwurf enthält unten noch
   * die Münster-Kontaktdaten.
   * Deshalb wird nur dieser Bereich überdeckt.
   */

  coverWhite(page, 214, 14, 178, 33);

  const footerLine1 =
    `${location.name} | ${location.address}`;

  const footerLine2 =
    `${location.instagram} | ${location.email}`;

  const footerLine3 =
    `Tel.: ${location.phone} | ${location.website}`;

  const footerX = 214;
  const footerWidth = 178;

  const line1Size = fitFontSize(
    footerLine1,
    bold,
    footerWidth,
    5.6,
    4.1
  );

  const line2Size = fitFontSize(
    footerLine2,
    regular,
    footerWidth,
    5.6,
    4.1
  );

  const line3Size = fitFontSize(
    footerLine3,
    regular,
    footerWidth,
    5.6,
    4.1
  );

  drawCenteredText(
    page,
    footerLine1,
    bold,
    line1Size,
    footerX,
    34,
    footerWidth
  );

  drawCenteredText(
    page,
    footerLine2,
    regular,
    line2Size,
    footerX,
    26.7,
    footerWidth
  );

  drawCenteredText(
    page,
    footerLine3,
    regular,
    line3Size,
    footerX,
    19.4,
    footerWidth
  );
  /*
 * QR-Code unten rechts.
 * Der Link öffnet die Gutscheinübersicht
 * mit der Gutscheinnummer als Suchparameter.
 */

  const qrValue = buildQrValue(voucherNumber);
  const qrBytes = await createQrPngBytes(qrValue);
  const qrImage = await pdfDoc.embedPng(qrBytes);

  page.drawImage(qrImage, {
    x: 394,
    y: 18,
    width: 31,
    height: 31,
  });

  /*
   * Gutscheinnummer
   */

  page.drawText('Gutschein-Nr.:', {
    x: 391,
    y: 10.7,
    size: 4.6,
    font: regular,
    color: rgb(0.05, 0.05, 0.05),
  });

  const numberSize = fitFontSize(
    voucherNumber,
    regular,
    39,
    5.2,
    3.8
  );

  page.drawText(voucherNumber, {
    x: 391,
    y: 4.9,
    size: numberSize,
    font: regular,
    color: rgb(0.05, 0.05, 0.05),
  });

  /*
   * PDF-Metadaten
   */

  pdfDoc.setTitle(`Gutschein ${voucherNumber}`);
  pdfDoc.setSubject('Prime Ink Tattoo Gutschein');
  pdfDoc.setCreator('Prime Ink Booking App');
  pdfDoc.setProducer('Prime Ink Booking App');

  /*
   * Zuerst wird der fertig ausgefüllte Gutschein gespeichert.
   */
  const completedVoucherBytes = await pdfDoc.save();

  /*
   * Anschließend erzeugen wir ein echtes DIN-A4-Dokument hochkant.
   *
   * DIN A4 in PDF-Punkten:
   * 595.28 × 841.89 pt
   */
  const a4Document = await PDFDocument.create();

  const A4_WIDTH = 595.28;
  const A4_HEIGHT = 841.89;

  const a4Page = a4Document.addPage([
    A4_WIDTH,
    A4_HEIGHT,
  ]);

  /*
   * Den fertigen Gutschein als Vektorseite in das
   * DIN-A4-Dokument einbetten.
   */
  const sourceDocument = await PDFDocument.load(
    completedVoucherBytes
  );

  const [embeddedVoucher] =
    await a4Document.embedPdf(sourceDocument, [0]);

  const originalWidth = embeddedVoucher.width;
  const originalHeight = embeddedVoucher.height;

  /*
   * Gutschein über die volle DIN-A4-Breite skalieren.
   * Das Seitenverhältnis bleibt erhalten.
   */
  const scale = A4_WIDTH / originalWidth;

  const voucherWidth = A4_WIDTH;
  const voucherHeight = originalHeight * scale;

  /*
   * Horizontal randlos.
   * Vertikal mittig auf dem DIN-A4-Blatt.
   */
  const voucherX = 0;
  const voucherY = (A4_HEIGHT - voucherHeight) / 2;

  a4Page.drawPage(embeddedVoucher, {
    x: voucherX,
    y: voucherY,
    width: voucherWidth,
    height: voucherHeight,
  });

  /*
   * Metadaten auch für die finale DIN-A4-Datei setzen.
   */
  a4Document.setTitle(`Gutschein ${voucherNumber}`);
  a4Document.setSubject('Prime Ink Tattoo Gutschein');
  a4Document.setCreator('Prime Ink Booking App');
  a4Document.setProducer('Prime Ink Booking App');

  const pdfBytes = await a4Document.save();

  return pdfBytes;
}

export async function openVoucherPdf(voucher) {
  const pdfBytes = await createVoucherPdfBytes(voucher);

  const blob = new Blob([pdfBytes], {
    type: 'application/pdf',
  });

  const url = URL.createObjectURL(blob);

  const pdfWindow = window.open(
    url,
    '_blank',
    'noopener,noreferrer'
  );

  if (!pdfWindow) {
    const link = document.createElement('a');

    link.href = url;
    link.download =
      `Gutschein-${getVoucherNumber(voucher)}.pdf`;

    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60000);
}

export {
  getVoucherNumber,
};